import type { LocalizedString } from 'typesafe-i18n';

import { BankDocumentTemplateV5 } from '../../../api/generated/cz.partners.bank.backofficegateway.documents';
import { useI18nContext } from '../../../locale/i18n-react';

export const useBankDocumentTypeTranslationMap = () => {
	const { LL } = useI18nContext();

	const typeTranslationMap: Record<BankDocumentTemplateV5, LocalizedString> = {
		[BankDocumentTemplateV5.ADDENDUM_TO_FRAMEWORK_AGREEMENT]:
			LL.documents.bankTypes.addendumToFrameworkAgreement(),
		[BankDocumentTemplateV5.AVAILABLE_ACCOUNT_CONFIRMATION]:
			LL.documents.bankTypes.availableAccountConfirmation(),
		[BankDocumentTemplateV5.AVAILABLE_BALANCE_CONFIRMATION]:
			LL.documents.bankTypes.availableBalanceConfirmation(),
		[BankDocumentTemplateV5.COMMITMENTS_CONFIRMATION]:
			LL.documents.bankTypes.commitmentsConfirmation(),
		[BankDocumentTemplateV5.COMPLAINTS_PROCEDURE]:
			LL.documents.bankTypes.complaintsProcedure(),
		[BankDocumentTemplateV5.DIRECT_DEBIT_CONFIRMATION]:
			LL.documents.bankTypes.directDebitConfirmation(),
		[BankDocumentTemplateV5.FOREIGN_POLICE_CONFIRMATION]:
			LL.documents.bankTypes.foreignPoliceConfirmation(),
		[BankDocumentTemplateV5.FRAMEWORK_AGREEMENT]:
			LL.documents.bankTypes.frameworkAgreement(),
		[BankDocumentTemplateV5.PRICE_LIST]: LL.documents.bankTypes.priceList(),
		[BankDocumentTemplateV5.SIPO_CONFIRMATION]:
			LL.documents.bankTypes.sipoConfirmation(),
		[BankDocumentTemplateV5.STANDING_ORDER_CONFIRMATION]:
			LL.documents.bankTypes.standingOrderConfirmation(),
		[BankDocumentTemplateV5.STATEMENT_OF_FEES]:
			LL.documents.bankTypes.statementOfFees(),
		[BankDocumentTemplateV5.STATEMENT_OF_PAYMENTS]:
			LL.documents.bankTypes.statementOfPayments(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS]:
			LL.documents.bankTypes.termsAndConditions(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_CARDS]:
			LL.documents.bankTypes.termsAndConditionsCards(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_CARDS_GOOGLE_AND_APPLE_PAY]:
			LL.documents.bankTypes.termsAndConditionsCardsGoogleAndApplePay(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_PAYMENTS]:
			LL.documents.bankTypes.termsAndConditionsPayments(),
		[BankDocumentTemplateV5.MOBILITY_REQUEST]:
			LL.documents.bankTypes.mobilityRequest(),
		[BankDocumentTemplateV5.INTEREST_RATES]:
			LL.documents.bankTypes.interestRates(),
		[BankDocumentTemplateV5.PERSONAL_DATA_PROCESSING_INFORMATION]:
			LL.documents.bankTypes.personalDataProcessingInformation(),
		[BankDocumentTemplateV5.PERMANENTLY_ACCESSIBLE_INFORMATION]:
			LL.documents.bankTypes.permanentlyAccessibleInformation(),
		[BankDocumentTemplateV5.TERMS_OF_USE_MOBILE_APPLICATION]:
			LL.documents.bankTypes.termsOfUseMobileApplication(),
		[BankDocumentTemplateV5.GROUP_MARKETING_CONSENT]:
			LL.documents.bankTypes.groupMarketingConsent(),
		[BankDocumentTemplateV5.INFORMATION_ON_THE_DEPOSIT_INSURANCE_SYSTEM]:
			LL.documents.bankTypes.informationOnTheDepositInsuranceSystem(),
		[BankDocumentTemplateV5.MOBILITY_CHANGE_ACCOUNT]:
			LL.documents.bankTypes.mobilityChangeAccount(),
		[BankDocumentTemplateV5.MOBILITY_PAYERS]:
			LL.documents.bankTypes.mobilityPayers(),
		[BankDocumentTemplateV5.CARD_PAYMENT_CONFIRMATION]:
			LL.documents.bankTypes.cardPaymentConfirmation(),
		[BankDocumentTemplateV5.DOMESTIC_PAYMENT_CONFIRMATION]:
			LL.documents.bankTypes.domesticPaymentConfirmation(),
		[BankDocumentTemplateV5.SEPA_PAYMENT_CONFIRMATION]:
			LL.documents.bankTypes.sepaPaymentConfirmation(),
		[BankDocumentTemplateV5.FOREIGN_PAYMENT_CONFIRMATION]:
			LL.documents.bankTypes.foreignPaymentConfirmation(),
		[BankDocumentTemplateV5.EXAMPLE_TEMPLATE]:
			LL.documents.bankTypes.exampleTemplate(),
		[BankDocumentTemplateV5.LOAN_INTEREST_RATE_GUARANTEE_UNSECURED]:
			LL.documents.bankTypes.loanInterestRateGuaranteeUnsecured(),
		[BankDocumentTemplateV5.LOAN_INTEREST_RATE_GUARANTEE_SECURED]:
			LL.documents.bankTypes.loanInterestRateGuaranteeSecured(),
		[BankDocumentTemplateV5.LOAN_IZU_UNSECURED]:
			LL.documents.bankTypes.loanIzuUnsecured(),
		[BankDocumentTemplateV5.LOAN_SCHEDULE_UNSECURED]:
			LL.documents.bankTypes.loanScheduleUnsecured(),
		[BankDocumentTemplateV5.LOAN_SCHEDULE_SECURED]:
			LL.documents.bankTypes.loanScheduleSecured(),
		[BankDocumentTemplateV5.LOAN_EUROPEAN_STANDARDISED_INFORMATION_SHEET_SECURED]:
			LL.documents.bankTypes.loanEuropeanStandardisedInformationSheetSecured(),
		[BankDocumentTemplateV5.LOAN_ACCESSION_TO_DEBT_AGREEMENT_SECURED]:
			LL.documents.bankTypes.loanAccessionToDebtAgreementSecured(),
		[BankDocumentTemplateV5.LOAN_ACCESSION_TO_DEBT_AGREEMENT_UNSECURED]:
			LL.documents.bankTypes.loanAccessionToDebtAgreementUnsecured(),
		[BankDocumentTemplateV5.LOAN_APPLICATION_UNSECURED]:
			LL.documents.bankTypes.loanApplicationUnsecured(),
		[BankDocumentTemplateV5.LOAN_APPLICATION_SECURED]:
			LL.documents.bankTypes.loanApplicationSecured(),
		[BankDocumentTemplateV5.LOAN_CALCULATION_WHEN_WITHDRAWING_FROM_A_LOAN]:
			LL.documents.bankTypes.loanCalculationWhenWithdrawingFromALoan(),
		[BankDocumentTemplateV5.LOAN_CONFIRMATION_OF_CANCELLATION_OF_APPLICATION]:
			LL.documents.bankTypes.loanConfirmationOfCancellationOfApplication(),
		[BankDocumentTemplateV5.LOAN_CONFIRMATION_OF_CONSUMER_LOAN_REPAYMENT]:
			LL.documents.bankTypes.loanConfirmationOfConsumerLoanRepayment(),
		[BankDocumentTemplateV5.LOAN_CONFIRMATION_OF_CONSUMER_LOAN_REPAYMENT_AND_TERMINATION_OF_THE_ACCESSION_OBLIGATION]:
			LL.documents.bankTypes.loanConfirmationOfConsumerLoanRepaymentAndTerminationOfTheAccessionObligation(),
		[BankDocumentTemplateV5.LOAN_CONTRACT_SECURED]:
			LL.documents.bankTypes.loanContractSecured(),
		[BankDocumentTemplateV5.LOAN_CONTRACT_UNSECURED]:
			LL.documents.bankTypes.loanContractUnsecured(),
		[BankDocumentTemplateV5.LOAN_EUROPEAN_STANDARDISED_INFORMATION_SHEET_UNSECURED]:
			LL.documents.bankTypes.loanEuropeanStandardisedInformationSheetUnsecured(),
		[BankDocumentTemplateV5.LOAN_EXTRAORDINARY_FULL_INSTALMENT_CALCULATION]:
			LL.documents.bankTypes.loanExtraordinaryFullInstalmentCalculation(),
		[BankDocumentTemplateV5.LOAN_NOTICE_OF_EARLY_REPAYMENT_OF_CONSUMER_LOAN_UNSECURED]:
			LL.documents.bankTypes.loanNoticeOfEarlyRepaymentOfConsumerLoanUnsecured(),
		[BankDocumentTemplateV5.LOAN_PRE_CONTRACTUAL_EXPLANATION]:
			LL.documents.bankTypes.loanPreContractualExplanation(),
		[BankDocumentTemplateV5.LOAN_STANDARD_EUROPEAN_CONSUMER_CREDIT_INFORMATION_UNSECURED]:
			LL.documents.bankTypes.loanStandardEuropeanConsumerCreditInformationUnsecured(),
		[BankDocumentTemplateV5.LOAN_CONFIRMATION_OF_PAID_INTEREST_SECURED]:
			LL.documents.bankTypes.loanConfirmationOfPaidInterestSecured(),
		[BankDocumentTemplateV5.LOAN_AGREEMENT]:
			LL.documents.bankTypes.loanAgreement(),
		[BankDocumentTemplateV5.LOAN_ACCESSOR_AGREEMENT]:
			LL.documents.bankTypes.loanAccessorAgreement(),
		[BankDocumentTemplateV5.LOAN_REFINANCING_TERMINATION]:
			LL.documents.bankTypes.loanRefinancingTermination(),
		[BankDocumentTemplateV5.LOAN_EARLY_REPAYMENT_NOTICE]:
			LL.documents.bankTypes.loanEarlyRepaymentNotice(),
		[BankDocumentTemplateV5.FRAMEWORK_AGREEMENT_AMENDMENT]:
			LL.documents.bankTypes.frameworkAgreementAmendment(),
		[BankDocumentTemplateV5.PRICE_LIST_AMENDMENT]:
			LL.documents.bankTypes.priceListAmendment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_AMENDMENT]:
			LL.documents.bankTypes.termsAndConditionsAmendment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_ADJUSTMENT]:
			LL.documents.bankTypes.termsAndConditionsAdjustment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_PAYMENTS_AMENDMENT]:
			LL.documents.bankTypes.termsAndConditionsPaymentsAmendment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_PAYMENTS_ADJUSTMENT]:
			LL.documents.bankTypes.termsAndConditionsPaymentsAdjustment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_CARDS_AMENDMENT]:
			LL.documents.bankTypes.termsAndConditionsCardsAmendment(),
		[BankDocumentTemplateV5.TERMS_AND_CONDITIONS_CARDS_ADJUSTMENT]:
			LL.documents.bankTypes.termsAndConditionsCardsAdjustment(),
		[BankDocumentTemplateV5.ACCOUNT_TERMINATION_BANK_IMMEDIATE_CAB]:
			LL.documents.bankTypes.accountTerminationBankImmediateCab(),
		[BankDocumentTemplateV5.ACCOUNT_TERMINATION_BANK_IMMEDIATE_OTHER]:
			LL.documents.bankTypes.accountTerminationBankImmediateOther(),
		[BankDocumentTemplateV5.ACCOUNT_TERMINATION_BANK_WITH_NOTICE_PERIOD]:
			LL.documents.bankTypes.accountTerminationBankWithNoticePeriod(),
		[BankDocumentTemplateV5.ACCOUNT_TERMINATION_LIMITATION_OF_AUTONOMY]:
			LL.documents.bankTypes.accountTerminationLimitationOfAutonomy(),
		[BankDocumentTemplateV5.OTHER]: LL.documents.bankTypes.other(),
	};

	return typeTranslationMap;
};
